import { array, arrayOf, bool, func, object, oneOf, shape, string } from 'prop-types';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { compose } from 'redux';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { isBookingProcess, resolveLatestProcessName } from '../../transactions/transaction';
import { convertMoneyToNumber } from '../../util/currency';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { FormattedMessage, intlShape, useIntl } from '../../util/reactIntl';
import { richText } from '../../util/richText';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_STATE_CLOSED,
  LISTING_STATE_PENDING_APPROVAL,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  propTypes,
} from '../../util/types';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  createSlug,
  getIframeParentPage,
  hasQueryParam,
} from '../../util/urlHelpers';
import {
  getCategoryLabel,
  getSubcategoryLabel,
  normalizeLegacySubcategorySlug,
} from '../../util/storeTaxonomy';
import { extractYoutubeVideoId } from '../../util/youtube';

import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import { fetchStoresLean } from '../../ducks/user.duck';

import {
  H4,
  HistoricalPriceChart,
  LayoutSingleColumn,
  Modal,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  Page,
} from '../../components';

import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import {
  fetchTimeSlots,
  fetchTransactionLineItems,
  sendInquiry,
  sendInquiryOffer,
  setInitialValues,
  fetchRelatedListings,
  fetchSimilarListings,
} from './ListingPage.duck';

import InquiryOfferForm from './InquiryOfferForm/InquiryOfferForm.js';
import {
  ErrorPage,
  LoadingPage,
  handleContactUser,
  handleSubmit,
  handleSubmitInquiry,
  listingImages,
  priceData,
} from './ListingPage.shared';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionDetailsMaybe from './SectionDetailsMaybe';
import SectionHero from './SectionHero';
import SectionMapMaybe from './SectionMapMaybe';
import SectionMultiEnumMaybe from './SectionMultiEnumMaybe';
import SectionReviews from './SectionReviews';
import SectionTextMaybe from './SectionTextMaybe';
import SectionThreeColumns from './SectionThreeColumns';
import SectionConditionTags from './SectionConditionTags';
import SectionSpecifications, { hasSpecifications } from './SectionSpecifications';
import SectionDeliveryOptions from './SectionDeliveryOptions';
import ProductImageGallery from './ProductImageGallery';
import PriceBadge from './PriceBadge';
import SectionFeaturedListings from '../LandingPage/sections/SectionFeaturedListings';

import css from './ListingPage.module.css';
import ActionBarMaybe from './ActionBarMaybe.js';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

const formatServiceLocation = address => {
  if (!address) return null;

  const parts = address
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);

  if (parts.length === 0) {
    return { cityState: '', fullAddress: address };
  }

  const filtered = parts.filter(part => !/^(United States|USA|US|Canada|Mexico)$/i.test(part));
  const locationParts = filtered.length > 0 ? filtered : parts;
  let cityState = '';

  if (locationParts.length >= 2) {
    const city = locationParts[locationParts.length - 2];
    const stateZip = locationParts[locationParts.length - 1].replace(/\s*\d{5}(-\d{4})?$/, '').trim();
    cityState = stateZip ? `${city}, ${stateZip}` : city;
  } else {
    cityState = locationParts[0]?.replace(/\s*\d{5}(-\d{4})?$/, '').trim() || '';
  }

  return {
    cityState,
    fullAddress: address,
  };
};

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [imageCarouselOpen, setImageCarouselOpen] = useState(false);
  const [pageUrl, setPageUrl] = useState('');
  const [showOfferModal, setShowOfferModal] = useState(false);
  // Mobile-only entry point for description + specifications, which are hidden inline below 768px.
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const [selectedDelivery, setSelectedDelivery] = useState('pickup');
  const [manufacturerImagesLoading, setManufacturerImagesLoading] = useState(false);

  // Fetch stores for the "Sellers Live on Bowtackle" section (same data as landing page dealers)
  useEffect(() => {
    onFetchStores();
  }, []);

  useEffect(() => {
    // Check if window exists (for SSR compatibility)
    if (typeof window === 'undefined') return;

    let parentPage = getIframeParentPage(window.location?.hostname);
    parentPage = parentPage ? `${parentPage}?target=` : '';
    let currentLocation = parentPage + window.location.href.split('?')[0] + '?iframe';
    setPageUrl(currentLocation);
  }, []);

  useEffect(() => {
    if (props.domLoaded && (props?.listings || []).length && !props.firstLoaded) {
      props.handleFirstLoaded();
      const creatorsId = (props?.listings || []).map(l => l?.attributes?.publicData?.creatorId).filter(l => l);
      props.handleCreators(creatorsId);
    }
  }, [props?.listings]);

  // ⚡ SIMPLIFIED APPROACH - Only essential hooks at the top

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews,
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    monthlyTimeSlots,
    onFetchTimeSlots,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    authorId,
    creator,
    onSendInquiryOffer,
    parentImages,
    relatedListings,
    fetchRelatedListingsInProgress,
    onFetchRelatedListings,
    similarListings,
    onFetchSimilarListings,
    stores,
    onFetchStores,
  } = props;

  // Keep iframe detection stable for the whole component lifecycle
  const isIframe =
    hasQueryParam(props.location, 'iframe') ||
    (typeof window !== 'undefined' && window.self !== window.top);

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));
  const pickupEnabled = currentListing?.attributes?.publicData?.pickupEnabled;
  const shippingEnabled = currentListing?.attributes?.publicData?.shippingEnabled;

  // Sync once the resolved listing data is available so the initial selection
  // matches the actual delivery capabilities of the listing being viewed.
  useEffect(() => {
    if (pickupEnabled !== undefined || shippingEnabled !== undefined) {
      setSelectedDelivery(pickupEnabled ? 'pickup' : 'shipping');
    }
  }, [currentListing?.id?.uuid, pickupEnabled, shippingEnabled]);

  // Get images to show: prefer listing.images, else parentImages
  const imagesToShow = useMemo(() => {
    const hasOwnImages = currentListing.images && currentListing.images.length > 0;
    const hasParentImages = parentImages && parentImages.length > 0;

    // Si tiene imágenes propias, usarlas
    if (hasOwnImages) {
      return currentListing.images;
    }

    // Si no tiene imágenes propias pero tiene imágenes del padre, usarlas
    if (hasParentImages) {
      return parentImages;
    }

    // Si no tiene ninguna imagen, retornar array vacío
    return [];
  }, [currentListing.images, parentImages]);

  // Detectar cuando se están cargando imágenes de manufacturers
  useEffect(() => {
    const { isFromManufacturer, manufacturerListingId } = currentListing?.attributes?.publicData || {};
    const hasImages = currentListing?.images && currentListing.images.length > 0;
    const hasParentImages = parentImages && parentImages.length > 0;

    // Si es de manufacturer, no tiene imágenes propias, y no tiene imágenes del padre aún
    if (isFromManufacturer && manufacturerListingId && !hasImages && !hasParentImages) {
      setManufacturerImagesLoading(true);
    } else {
      setManufacturerImagesLoading(false);
    }
  }, [parentImages, currentListing]);

  // Fetch related listings from manufacturer or dealer
  useEffect(() => {
    const { isFromManufacturer, manufacturerListingId } = currentListing?.attributes?.publicData || {};
    const authorId = currentListing?.author?.id?.uuid;

    if (currentListing?.id && onFetchRelatedListings) {
      const searchParams = {
        isFromManufacturer,
        manufacturerListingId,
        authorId,
      };
      onFetchRelatedListings(searchParams, currentListing.id, config);
    }
  }, [
    currentListing?.id,
    currentListing?.attributes?.publicData?.isFromManufacturer,
    currentListing?.attributes?.publicData?.manufacturerListingId,
    currentListing?.author?.id?.uuid,
    config,
    onFetchRelatedListings,
  ]);

  // Fetch "You May Also Like" listings (same subcategory, different seller)
  useEffect(() => {
    const { subcategory } = currentListing?.attributes?.publicData || {};
    const authorId = currentListing?.author?.id?.uuid;
    const listingType = currentListing?.attributes?.publicData?.listingType;
    if (listingType !== 'services' && currentListing?.id && subcategory && onFetchSimilarListings) {
      onFetchSimilarListings(subcategory, authorId, currentListing.id, config);
    }
  }, [
    currentListing?.id,
    currentListing?.attributes?.publicData?.listingType,
    currentListing?.attributes?.publicData?.subcategory,
    currentListing?.author?.id?.uuid,
    config,
    onFetchSimilarListings,
  ]);

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage />;
  } else if (showListingError && showListingError.status === 410) {
    // 410 Gone - seller not approved, redirect to home
    return <NamedRedirect name="LandingPage" />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} isIframe={isIframe} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} isIframe={isIframe} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const transactionProcessAlias = publicData?.transactionProcessAlias;
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isServiceListing = publicData?.listingType === 'services';
  const processType = isBooking ? 'booking' : 'purchase';
  const serviceLocation = formatServiceLocation(publicData?.location?.address);

  // Description and specifications are rendered inline on desktop (see .hideOnMobile below).
  // On mobile, including the dealer iframe, they live behind a single button that opens them
  // in a full-screen modal. The live dealer host uses a fixed-height iframe with internal
  // scrolling, so Modal's fixed positioning is anchored to the visible iframe viewport.
  const fullDescriptionLabel = isServiceListing
    ? 'Full Service Description'
    : 'Full Product Description';
  const showSpecifications = !isServiceListing && hasSpecifications(publicData);
  const showDetailsButton = !!description || showSpecifications;

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && !currentUser?.attributes?.stripeConnected;
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <div>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </div>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
    isIframe,
  });
  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
    isIframe,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  // ⚡ PERFORMANCE OPTIMIZATIONS - Without problematic hooks
  const createFilterOptions = options => options.map(o => ({ key: `${o.option}`, label: o.label }));

  const handleViewPhotosClick = e => {
    // Stop event from bubbling up to prevent image click handler
    // trying to open the carousel as well.
    e.stopPropagation();
    setImageCarouselOpen(true);
  };

  const youtubeVideo = publicData?.youtubeVideo;
  const videoKey = extractYoutubeVideoId(youtubeVideo);

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      let { deliveryMethod, quantity } = values || {};
      let listingId = currentListing?.id?.uuid;

      if (deliveryMethod) {
        plausible(
          `deliveryMethod${deliveryMethod.charAt(0).toUpperCase()}${deliveryMethod.slice(1)}`,
          { props: { listingId } }
        );
      }
      plausible('buyNow', { props: { listingId, quantity } });
      onSubmit({ ...values, location });
    }
  };

  const normalizedSubcategory = normalizeLegacySubcategorySlug(publicData?.subcategory);
  const breadcrumbCategory = isServiceListing ? null : getCategoryLabel(publicData?.category);
  const breadcrumbSubcategory = isServiceListing ? null : getSubcategoryLabel(publicData?.subcategory);

  // ⚡ OPTIMIZED: Compute expensive operations only once per render
  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);

  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const schemaPriceMaybe = price
    ? {
        price: intl.formatNumber(convertMoneyToNumber(price), {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        priceCurrency: price.currency,
      }
    : {};
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability =
    currentStock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock';
  const pageSchema = isServiceListing
    ? undefined
    : {
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...schemaPriceMaybe,
          availability: schemaAvailability,
        },
      };

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      socialSharing={{
        title: title,
        description: description,
        images1200: facebookImages,
        images600: twitterImages,
      }}
      schema={pageSchema}
    >
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={<TopbarContainer />}
        footer={isIframe ? <></> : <FooterContainer />}
      >
         {!isIframe && currentListing.id && noPayoutDetailsSetWithOwnListing ? (
                <ActionBarMaybe
                            className={css.actionBarForProductLayout}
                            isOwnListing={isOwnListing}
                            listing={currentListing}
                            showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
                          />
                        ) : null}
        <Modal
          id="OfferModal"
          isOpen={showOfferModal}
          onClose={() => setShowOfferModal(false)}
          onManageDisableScrolling={onManageDisableScrolling}
          className={css.offerModal}
        >
          <InquiryOfferForm
            className={css.offerForm}
            listingTitle={title}
            authorDisplayName={authorDisplayName}
            sendInquiryError={sendInquiryError}
            inProgress={sendInquiryInProgress}
            config={config}
            onSubmit={values => {
              const { offer, message } = values;

              const { formattedPrice } = priceData(offer, config.currency, intl);

              return onSendInquiryOffer(
                currentListing,
                { offer },
                message ||
                  intl.formatMessage(
                    { id: 'InquiryOfferForm.defaultMessage' },
                    {
                      listingTitle: title,
                      authorDisplayName,
                      formattedPrice,
                    }
                  )
              )
              .then(txId => {
                console.log('SUCCESS: Inquiry sent, closing modal');
                setShowOfferModal(false);
                return txId;
              })
              .catch(e => {
                console.log('ERROR: Inquiry failed, closing modal', e);
                setShowOfferModal(false);
                throw e;
              });
            }}
          />
        </Modal>
        {/* Mobile-only: description + specifications behind a full-screen modal.
            The scroll lives on the content element so the close button stays pinned. */}
        <Modal
          id="ListingDetailsModal"
          isOpen={showDetailsButton && showDetailsModal}
          onClose={() => setShowDetailsModal(false)}
          onManageDisableScrolling={onManageDisableScrolling}
          usePortal
          containerClassName={css.detailsModalContainer}
          contentClassName={css.detailsModalContent}
        >
          <h2 className={css.detailsModalHeading}>{fullDescriptionLabel}</h2>
          <div className={css.detailsModalBody}>
            <SectionTextMaybe text={description} showAsIngress />
            {showSpecifications ? (
              <SectionSpecifications publicData={publicData} listingConfig={listingConfig} />
            ) : null}
          </div>
        </Modal>
        {/* Breadcrumb */}
        <div className={css.breadcrumb}>
          {breadcrumbCategory && (
            <>
              <span className={css.breadcrumbItem}>{breadcrumbCategory}</span>
              <span className={css.breadcrumbSep}>›</span>
            </>
          )}
          {breadcrumbSubcategory && (
            <>
              <span className={css.breadcrumbItem}>{breadcrumbSubcategory}</span>
              <span className={css.breadcrumbSep}>›</span>
            </>
          )}
          <span className={css.breadcrumbCurrent}>{title}</span>
        </div>

        <div className={css.contentWrapperForHeroLayout}>
          {/* LEFT COLUMN - Product Images */}
          <div className={css.mainColumnForHeroLayout}>
            <div className={css.imageGalleryWrapper}>
              <ProductImageGallery
                listing={{ ...currentListing, images: imagesToShow }}
                handleViewPhotosClick={handleViewPhotosClick}
              />
            </div>
          </div>

          {/* RIGHT COLUMN - All product info */}
          <div className={css.orderColumnForHeroLayout}>
            {/* Product-only brand/manufacturer chip */}
            {!isServiceListing && (publicData.brand || publicData.manufacturer) && (
              <div className={css.madeByWrapper}>
                <span className={css.madeByLabel}>Made by </span>
                {isIframe ? (
                  <span className={css.madeByBrand}>
                    {publicData.brand || publicData.manufacturer}
                  </span>
                ) : (
                  <a
                    className={css.madeByBrand}
                    href={`/s?pub_brand=${encodeURIComponent(publicData.brand || publicData.manufacturer)}`}
                  >
                    {publicData.brand || publicData.manufacturer}
                  </a>
                )}
              </div>
            )}

            <div className={css.productTitleWrapper}>
              <H4 as="h1" className={css.productTitle}>
                <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
              </H4>
            </div>

            <PriceBadge price={price} intl={intl} />

            {!isServiceListing ? <SectionConditionTags publicData={publicData} /> : null}

            {showDetailsButton ? (
              <div className={css.showOnMobile}>
                <button
                  type="button"
                  className={css.detailsModalButton}
                  onClick={() => setShowDetailsModal(true)}
                >
                  <span className={css.detailsModalButtonLabel}>
                    {showSpecifications
                      ? `${fullDescriptionLabel} & Specifications`
                      : fullDescriptionLabel}
                  </span>
                  <span className={css.detailsModalButtonChevron} aria-hidden="true">
                    ›
                  </span>
                </button>
              </div>
            ) : null}

            <div>
              <p className={css.fulfilledByLabel}>
                {isServiceListing ? 'Provided by:' : 'Fulfilled by:'}
              </p>
              <SectionThreeColumns
                publicData={publicData}
                author={ensuredAuthor}
                listing={currentListing}
                creator={creator}
                reviews={reviews}
                isIframe={isIframe}
                showLocation={!isServiceListing}
              />
              {!isIframe && (
                <div className={css.dealerActions}>
                  <button className={css.dealerActionLink} onClick={onContactUser}>
                    Message Dealer
                  </button>
                  <a
                    className={css.dealerActionLink}
                    href={`/store/${ensuredAuthor?.id?.uuid}/detail#listings`}
                  >
                    See All Listings
                  </a>
                </div>
              )}
            </div>

            {isServiceListing && serviceLocation ? (
              <div className={css.serviceLocationBlock}>
                <p className={css.serviceLocationLabel}>Service Location</p>
                {serviceLocation.cityState ? (
                  <p className={css.serviceLocationCityState}>{serviceLocation.cityState}</p>
                ) : null}
                <p className={css.serviceLocationAddress}>{serviceLocation.fullAddress}</p>
              </div>
            ) : null}

            {!isServiceListing ? (
              <div className={css.deliverySection}>
                <hr className={css.mobileDivider} />
                <SectionDeliveryOptions
                  publicData={publicData}
                  author={ensuredAuthor}
                  selectedDelivery={selectedDelivery}
                  onDeliveryChange={setSelectedDelivery}
                />
                <hr className={css.mobileDivider} />
              </div>
            ) : null}

            <OrderPanel
                className={css.orderPanel}
                showSupportChat
                listing={currentListing}
                isOwnListing={isOwnListing}
                onSubmit={handleOrderSubmit}
                isIframe={isIframe}
                pageUrl={pageUrl}
                showOfferModal={showOfferModal}
                setShowOfferModal={setShowOfferModal}
                authorLink={
                  <NamedLink
                    className={css.authorNameLink}
                    name="ListingPage"
                    params={params}
                    to={{ hash: '#author', search: isIframe ? 'iframe' : '' }}
                  >
                    {authorDisplayName}
                  </NamedLink>
                }
                title={null}
                titleDesktop={null}
                payoutDetailsWarning={payoutDetailsWarning}
                author={ensuredAuthor}
                onManageDisableScrolling={onManageDisableScrolling}
                onFetchTransactionLineItems={onFetchTransactionLineItems}
                onContactUser={onContactUser}
                monthlyTimeSlots={monthlyTimeSlots}
                onFetchTimeSlots={onFetchTimeSlots}
                lineItems={lineItems}
                fetchLineItemsInProgress={fetchLineItemsInProgress}
                fetchLineItemsError={fetchLineItemsError}
                validListingTypes={config.listing.listingTypes}
                marketplaceCurrency={config.currency}
                dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
                marketplaceName={config.marketplaceName}
                metadata={metadata}
                listingConfig={listingConfig}
                intl={intl}
                fromListingPage={true}
                currentUser={currentUser}
                selectedDelivery={selectedDelivery}
              />

            {/* Inline on desktop; mobile uses the details modal in every runtime. */}
            <div className={css.hideOnMobile}>
              <h3 className={css.fullDescriptionLabel}>{fullDescriptionLabel}</h3>
              <SectionTextMaybe text={description} showAsIngress />
            </div>

            {!isServiceListing ? (
              <div className={css.hideOnMobile}>
                <SectionSpecifications
                  publicData={publicData}
                  listingConfig={listingConfig}
                />
              </div>
            ) : null}
          </div>
        </div>

        {/* More From [Dealer] — desktop only */}
        {!isIframe && relatedListings && relatedListings.length > 0 && (() => {
          const { isFromManufacturer, manufacturerName } = publicData || {};
          const sellerName = (isFromManufacturer && manufacturerName) ? manufacturerName : authorDisplayName;
          const authorId = currentListing?.author?.id?.uuid;
          const filterUrl = authorId ? `/s?authorId=${authorId}` : '/s';
          return (
            <div className={`${css.featuredListingsSection} ${css.hideOnMobile}`}>
              <SectionFeaturedListings
                listings={relatedListings}
                title={`More From ${sellerName}`}
                filterUrl={filterUrl}
                intl={intl}
              />
            </div>
          );
        })()}

        {/* You May Also Like — desktop only */}
        {!isIframe && !isServiceListing && similarListings && similarListings.length > 0 && (() => {
          const filterUrl = normalizedSubcategory
            ? `/s?pub_subcategory=${encodeURIComponent(normalizedSubcategory)}`
            : '/s';
          return (
            <div className={`${css.featuredListingsSection} ${css.hideOnMobile}`}>
              <SectionFeaturedListings
                listings={similarListings}
                title="You May Also Like"
                filterUrl={filterUrl}
                intl={intl}
              />
            </div>
          );
        })()}

        {/* Mobile sections — listings grid + sellers grid */}
        {!isIframe && (() => {
          const allListings = [
            ...(relatedListings || []),
            ...(isServiceListing ? [] : similarListings || []),
          ].filter((l, i, arr) => arr.findIndex(x => x.id?.uuid === l.id?.uuid) === i);

          // Build sellers list from stores (same source as landing page dealers section)
          let storesArray = [];
          if (stores?.data) {
            storesArray = Object.values(stores.data);
          } else if (Array.isArray(stores)) {
            storesArray = stores;
          }
          const featuredSellers = storesArray
            .sort((a, b) => (b.listings?.length || 0) - (a.listings?.length || 0))
            .slice(0, 4);

          if (!allListings.length && !featuredSellers.length) return null;

          const fmtLocation = addr => {
            if (!addr) return '';
            const parts = addr.split(',').map(p => p.trim()).filter(Boolean)
              .filter(p => !/^(United States|USA|US|Canada|Mexico)$/i.test(p));
            if (parts.length >= 2) {
              const city = parts[parts.length - 2];
              const state = parts[parts.length - 1].replace(/\s*\d{5}(-\d{4})?$/, '').trim();
              return state ? `${city}, ${state}` : city;
            }
            return parts[0]?.replace(/\s*\d{5}(-\d{4})?$/, '').trim() || '';
          };

          const listingsFilterUrl = normalizedSubcategory
            ? `/s?pub_subcategory=${encodeURIComponent(normalizedSubcategory)}`
            : '/s';

          return (
            <div className={css.mobileSections}>

              {/* Listings grid */}
              {allListings.length > 0 && (
                <div className={css.mobileSectionBlock}>
                  <div className={css.mobileSectionHeader}>
                    <span className={css.mobileSectionTitle}>Listings</span>
                    <a href={listingsFilterUrl} className={css.mobileActionBtn}>Filter &amp; Sort</a>
                  </div>
                  <div className={css.mobileListingsGrid}>
                    {allListings.slice(0, 6).map(l => {
                      const img = l.images?.[0];
                      const variants = img?.attributes?.variants || {};
                      const variantKey =
                        Object.keys(variants).find(k => k.includes('listing-card')) ||
                        Object.keys(variants).find(k => k.includes('scaled')) ||
                        Object.keys(variants)[0];
                      const imgUrl = variantKey ? variants[variantKey]?.url : null;
                      const lTitle = l.attributes?.title || '';
                      const lPrice = l.attributes?.price;
                      const lCondition = l.attributes?.publicData?.condition;
                      const lDealer =
                        l.author?.attributes?.profile?.publicData?.businessName ||
                        l.author?.attributes?.profile?.displayName || '';
                      const lLocation = fmtLocation(
                        l.author?.attributes?.profile?.publicData?.storedAddress?.place_name ||
                        l.attributes?.publicData?.location?.address || ''
                      );
                      return (
                        <a
                          key={l.id?.uuid}
                          href={`/l/${createSlug(lTitle)}/${l.id?.uuid}`}
                          className={css.mobileListingCard}
                        >
                          <div className={css.mobileCardImageWrap}>
                            {imgUrl && <img src={imgUrl} alt={lTitle} className={css.mobileCardImg} />}
                            {lCondition && (
                              <span className={css.mobileCardBadge}>{lCondition.toUpperCase()}</span>
                            )}
                          </div>
                          <div className={css.mobileCardBody}>
                            <p className={css.mobileCardTitle}>{lTitle}</p>
                            {lDealer && <p className={css.mobileCardDealer}>By {lDealer}</p>}
                            {lLocation && <p className={css.mobileCardLocation}>{lLocation}</p>}
                            {lPrice && (
                              <span className={css.mobileCardPrice}>
                                ${(lPrice.amount / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                              </span>
                            )}
                          </div>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Sellers grid */}
              {featuredSellers.length > 0 && (
                <div className={css.mobileSectionBlock}>
                  <div className={css.mobileSectionHeader}>
                    <span className={css.mobileSectionTitle}>Sellers Live on Bowtackle</span>
                    <a href="/dealers" className={css.mobileActionBtn}>Search All Sellers</a>
                  </div>
                  <div className={css.mobileSellersGrid}>
                    {featuredSellers.map(seller => (
                      <SectionThreeColumns
                        key={seller?.id?.uuid}
                        publicData={{}}
                        author={seller}
                        reviews={[]}
                        compact
                      />
                    ))}
                  </div>
                </div>
              )}

            </div>
          );
        })()}
      </LayoutSingleColumn>
    </Page>
  );
};

ListingPageComponent.defaultProps = {
  currentUser: null,
  inquiryModalOpenForListingId: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  monthlyTimeSlots: null,
  sendInquiryError: null,
  lineItems: null,
  fetchLineItemsError: null,
};

ListingPageComponent.propTypes = {
  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,
  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  params: shape({
    id: string.isRequired,
    slug: string,
    variant: oneOf([LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT]),
  }).isRequired,

  isAuthenticated: bool.isRequired,
  currentUser: propTypes.currentUser,
  getListing: func.isRequired,
  getOwnListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  scrollingDisabled: bool.isRequired,
  inquiryModalOpenForListingId: string,
  showListingError: propTypes.error,
  callSetInitialValues: func.isRequired,
  reviews: arrayOf(propTypes.review),
  fetchReviewsError: propTypes.error,
  monthlyTimeSlots: object,
  // monthlyTimeSlots could be something like:
  // monthlyTimeSlots: {
  //   '2019-11': {
  //     timeSlots: [],
  //     fetchTimeSlotsInProgress: false,
  //     fetchTimeSlotsError: null,
  //   }
  // }
  sendInquiryInProgress: bool.isRequired,
  sendInquiryError: propTypes.error,
  onSendInquiry: func.isRequired,
  onInitializeCardPaymentData: func.isRequired,
  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  authorId: string,
  creator: string,
  parentImages: array,
  relatedListings: array,
  fetchRelatedListingsInProgress: bool,
  onFetchRelatedListings: func,
};

const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    authorId,
    creator,
    parentImages,
    relatedListings,
    fetchRelatedListingsInProgress,
    similarListings,
  } = state.ListingPage;
  const { currentUser, stores } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    sendInquiryInProgress,
    sendInquiryError,
    authorId,
    creator,
    parentImages,
    relatedListings,
    fetchRelatedListingsInProgress,
    similarListings,
    stores,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)),
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone)),
  onSendInquiryOffer: (listing, offer, message) =>
    dispatch(sendInquiryOffer(listing, offer, message)),
  onFetchRelatedListings: (searchParams, currentListingId, config) =>
    dispatch(fetchRelatedListings(searchParams, currentListingId, config)),
  onFetchSimilarListings: (subcategory, authorId, currentListingId, config) =>
    dispatch(fetchSimilarListings(subcategory, authorId, currentListingId, config)),
  onFetchStores: () => dispatch(fetchStoresLean()),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedListingPage);

export default ListingPage;
